import { provideHttpClient, withInterceptors, withXhr } from "@angular/common/http";
import { ApplicationConfig, isDevMode, provideZoneChangeDetection } from "@angular/core";
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withRouterConfig } from "@angular/router";
import { AMPLITUDE_API_KEY_TOKEN, providePageViewTracking } from "@dcc/shared/amplitude";
import { AZURE_INSIGHTS_CONFIG, provideAzureInsights } from "@dcc/shared/application-insights";
import { provideFeatureFlags } from "@dcc/shared/feature-flags/data-access";
import { provideLocaleConfig } from "@dcc/shared/util-locale-manager";
import { DatepickerDefaults, provideUiKitDatepicker } from "@dcc/ui-kit/datepicker";
import { provideIcons } from "@dcc/vestas-design-system/angular/components/icon";
import { provideAuth, vestasOnlineAuthInterceptor } from "@dcc/vestas-online/auth/data-access";
import {
    provideAmplitudeExperiments,
    provideAmplitudeInitializationReady,
    provideAmplitudeWithVersion,
    provideCookieConsent,
    provideUserLocalization
} from "@dcc/vestas-online/data-access";
import { FeatureFlagsHttpLoader } from "@dcc/vestas-online/data-access-feature-flags";
import { APP_CONFIG, DEFAULT_DATE_FORMAT, VoAppConfig } from "@dcc/vestas-online/domain";
import { provideAmplitudeConsentInitializer } from "@dcc/vestas-online/feat-consent";
import { provideServiceWorkerCore } from "@dcc/vestas-online/feat-service-worker";
import { provideTranslocoSetup } from "@dcc/vestas-online/i18n/transloco-config";
import { provideUserTelemetry } from "@dcc/vestas-online/telemetry";
import { provideTranslocoLocale } from "@jsverse/transloco-locale";
import { provideHttpCache, withHttpCacheInterceptor } from "@ngneat/cashew";
import { provideHighcharts } from "highcharts-angular";
import { routes } from "./app.routes";
import { CustomDatePickerDefaults } from "./custom-datepicker-defaults";
import { provideHashbrownWithMsalMiddleware } from "@dcc/shared/hashbrown";
import { OVERLAY_DEFAULT_CONFIG } from "@angular/cdk/overlay";

export function createAppConfig(config: VoAppConfig): ApplicationConfig {
    return {
        providers: [
            provideZoneChangeDetection(),
            provideHighcharts(),
            provideHttpClient(withXhr(), withInterceptors([vestasOnlineAuthInterceptor, withHttpCacheInterceptor()])),
            provideHttpCache(),
            provideRouter(
                routes,
                // withDebugTracing(),
                withComponentInputBinding(),
                withInMemoryScrolling({ scrollPositionRestoration: "enabled" }),
                withRouterConfig({ paramsInheritanceStrategy: "always" })
            ),
            provideLocaleConfig({
                dateFormat: DEFAULT_DATE_FORMAT,
                timeZone: "UTC",
                availableLocales: {
                    de: () => import("date-fns/locale/de").then((locale) => locale.de),
                    es: () => import("date-fns/locale/es").then((locale) => locale.es),
                    ja: () => import("date-fns/locale/ja").then((locale) => locale.ja),
                    pl: () => import("date-fns/locale/pl").then((locale) => locale.pl),
                    "zh-cn": () => import("date-fns/locale/zh-CN").then((locale) => locale.zhCN),
                    "zh-tw": () => import("date-fns/locale/zh-TW").then((locale) => locale.zhTW)
                }
            }),
            provideUiKitDatepicker(),
            provideIcons({ url: "assets/svg/" }),
            provideAuth(config),
            provideCookieConsent(isDevMode()),
            provideUserTelemetry(),
            providePageViewTracking({ routeMetadata: true }),
            provideAzureInsights(isDevMode()),
            provideAmplitudeWithVersion(isDevMode(), {
                identityStorage: "sessionStorage",
                serverZone: "EU",
                optOut: false,
                autocapture: {
                    attribution: true,
                    pageViews: false,
                    sessions: true,
                    formInteractions: false,
                    fileDownloads: false
                }
            }),
            provideAmplitudeExperiments(isDevMode()),
            provideAmplitudeInitializationReady(isDevMode()),
            provideAmplitudeConsentInitializer(),
            provideHashbrownWithMsalMiddleware("/api/platform/chat"),
            provideTranslocoSetup(),
            provideUserLocalization(),
            provideTranslocoLocale(),
            provideFeatureFlags({ loader: FeatureFlagsHttpLoader }),
            ...(isDevMode() ? [] : [provideServiceWorkerCore()]),
            {
                provide: OVERLAY_DEFAULT_CONFIG,
                useValue: {
                    usePopover: false
                }
            },
            {
                provide: AZURE_INSIGHTS_CONFIG,
                useFactory: (appConfig: VoAppConfig) => appConfig.applicationInsights,
                deps: [APP_CONFIG]
            },
            {
                provide: AMPLITUDE_API_KEY_TOKEN,
                useFactory: (appConfig: VoAppConfig) => appConfig.amplitude.apiKey,
                deps: [APP_CONFIG]
            },
            // Provide custom datepicker defaults
            {
                provide: DatepickerDefaults,
                useClass: CustomDatePickerDefaults
            }
        ]
    };
}
